import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, MessageSquareText, Sparkles, Handshake, HardHat } from "lucide-react";
import SEO from "@/components/SEO";
import CTASection from "@/components/CTASection";
import { BUSINESS } from "@/lib/content";

const VALUES = [
  {
    icon: ShieldCheck,
    title: "Reliability",
    body: "We show up when we say we will, complete every scope in full, and take ownership of the finish.",
  },
  {
    icon: Sparkles,
    title: "Professionalism",
    body: "A calm, considered, presentable team — on every site, every clean, every time.",
  },
  {
    icon: MessageSquareText,
    title: "Communication",
    body: "Clear scope, clear pricing, direct point of contact. No surprises for you or your client.",
  },
  {
    icon: HardHat,
    title: "Attention to Detail",
    body: "The finish that most cleaners skip is the one commercial clients notice. That's where we live.",
  },
  {
    icon: Handshake,
    title: "Long-Term Partnership",
    body: "We want to become the trusted cleaning partner you rely on for years — not just the next job.",
  },
];

export default function About() {
  return (
    <>
      <SEO
        title="About Savage Cleaning — Commercial Cleaning Solutions Provider | SEQ"
        description="Savage Cleaning is a commercial cleaning solutions provider servicing Brisbane, the Gold Coast and South East Queensland."
      />

      {/* HERO */}
      <section className="relative py-24 sm:py-32 border-b border-white/5" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3">
              <span className="gold-rule" />
              <span className="eyebrow">About Savage Cleaning</span>
            </div>
            <h1 className="mt-6 font-serif-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.05]">
              Commercial Cleaning Solutions You Can Depend On.
            </h1>
            <p className="mt-8 text-lg text-white/85 leading-relaxed max-w-2xl">
              At Savage Cleaning, we believe exceptional cleaning is about more than presentation
              — it's about creating safe, professional environments that reflect the standards of
              your business.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBjbGVhbmVyJTIwd29ya2luZ3xlbnwwfHx8fDE3ODMzODkzMTZ8MA&ixlib=rb-4.1.0&q=85"
                alt="Professional commercial cleaning team"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 border border-[#C9A24B]/40" />
            </div>
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="py-24 sm:py-32" data-testid="about-story">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <span className="eyebrow">Our story</span>
              <h2 className="mt-4 font-serif-display text-4xl text-white leading-tight">
                Built for commercial. Trusted long-term.
              </h2>
            </div>
            <div className="md:col-span-8 space-y-6 text-white/85 leading-relaxed text-lg">
              <p>
                As a commercial cleaning solutions provider servicing Brisbane, the Gold Coast and
                South East Queensland, we work with builders, developers, property managers,
                healthcare providers, schools, childcare centres, retail businesses and commercial
                organisations to deliver consistently outstanding results.
              </p>
              <p>
                From builders' final cleans before handover to ongoing commercial maintenance
                contracts, our experienced team is equipped to deliver projects of every size with
                the same uncompromising commitment to quality.
              </p>
              <p>
                Alongside our commercial expertise, we also provide professional end-of-lease bond
                cleans and selected residential cleaning services for clients who expect the same
                exceptional standards.
              </p>
              <p className="text-white font-serif-display text-2xl leading-snug pt-4">
                Our goal isn't simply to clean your premises — it's to become the trusted cleaning
                partner you rely on for years to come.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24 sm:py-32" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="eyebrow">Our values</span>
            <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl text-white leading-tight">
              Five values, applied to every project.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {VALUES.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="card-dark"
                data-testid={`value-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon size={28} strokeWidth={1.4} className="text-[#C9A24B]" />
                <h3 className="mt-5 font-serif-display text-2xl text-white">{title}</h3>
                <p className="mt-3 text-white/75 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 border-l-2 border-[#C9A24B] pl-6 sm:pl-8 max-w-4xl">
            <p className="font-serif-display text-2xl sm:text-3xl text-white leading-snug">
              "{BUSINESS.tagline}"
            </p>
          </div>

          <div className="mt-16 text-center">
            <Link to="/contact" className="btn-primary" data-testid="about-cta-quote">
              Work with us <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <CTASection
        title="Ready to hand cleaning off to a team you can trust?"
        subtitle="Tell us about your site or contract and we'll come back with a clear scope and honest quote."
      />
    </>
  );
}
